import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Search, Heart } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { PRAYERS, PRAYER_CATEGORIES } from '@/lib/prayers';
import type { Prayer } from '@/lib/prayers';

export default function PrayersPage() {
  useSeoMeta({
    title: 'Prayers — Nostr Missal',
    description: 'Traditional Catholic prayers including the Rosary, Angelus, Divine Mercy Chaplet, and more.',
  });

  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    let result = PRAYERS;
    if (activeCategory !== 'All') {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.text.some((line) => line.toLowerCase().includes(q)) ||
          (p.subtitle && p.subtitle.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeCategory, search]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    PRAYERS.forEach((p) => {
      map[p.category] = (map[p.category] || 0) + 1;
    });
    return map;
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2.5 mb-1">
            <Heart className="h-5 w-5 text-stone-400 dark:text-stone-500" />
            <h1
              className="text-2xl font-semibold text-stone-800 dark:text-stone-200 sm:text-3xl"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              Prayers
            </h1>
          </div>
          <p className="text-sm text-stone-500 dark:text-stone-400 ml-7.5">
            Traditional Catholic prayers and devotions
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            type="text"
            placeholder="Search prayers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 text-stone-800 dark:text-stone-200 placeholder:text-stone-400"
          />
        </div>

        {/* Category tabs */}
        <div className="mb-6 flex flex-wrap gap-1.5">
          {PRAYER_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`rounded-full px-3.5 py-1.5 text-[12px] font-medium tracking-wide transition-all duration-150 ${
                activeCategory === cat
                  ? 'bg-stone-800 dark:bg-stone-200 text-white dark:text-stone-900'
                  : 'bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800'
              }`}
            >
              {cat}
              {cat !== 'All' && (
                <span className="ml-1.5 opacity-60">{counts[cat] || 0}</span>
              )}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-[11px] uppercase tracking-wider text-stone-400 dark:text-stone-500 mb-4">
          {filtered.length} prayer{filtered.length !== 1 ? 's' : ''}
        </p>

        {/* Prayers list */}
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900">
            <div className="flex flex-col items-center py-14 text-center px-6">
              <p className="max-w-xs text-sm text-stone-500 dark:text-stone-400 leading-relaxed">
                No prayers found. Try a different search or category.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((prayer) => (
              <PrayerCard key={prayer.id} prayer={prayer} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function PrayerCard({ prayer }: { prayer: Prayer }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
      >
        <div className="min-w-0">
          <h3
            className="text-[15px] font-semibold text-stone-800 dark:text-stone-200"
            style={{ fontFamily: '"Cinzel", serif' }}
          >
            {prayer.title}
          </h3>
          {prayer.subtitle && (
            <p className="text-[12px] text-stone-400 dark:text-stone-500 mt-0.5">
              {prayer.subtitle}
            </p>
          )}
        </div>
        <span className="ml-3 text-[11px] uppercase tracking-wider text-stone-400 dark:text-stone-500 shrink-0 hidden sm:inline">
          {prayer.category}
        </span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-0">
          <div className="border-t border-stone-100 dark:border-stone-800 pt-4 space-y-3">
            {prayer.traditional && (
              <div className="space-y-2">
                {prayer.traditional.map((line, i) => (
                  <p
                    key={`t-${i}`}
                    className="text-[15px] leading-[1.85] text-stone-700 dark:text-stone-300 whitespace-pre-wrap"
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}

            {!prayer.traditional && (
              <div className="space-y-2">
                {prayer.text.map((line, i) => (
                  <p
                    key={i}
                    className="text-[15px] leading-[1.85] text-stone-700 dark:text-stone-300 whitespace-pre-wrap"
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}

            {prayer.responses && (
              <div className="space-y-2 pt-1">
                {prayer.responses.map((line, i) => (
                  <p
                    key={`r-${i}`}
                    className="text-[14px] leading-[1.8] text-stone-600 dark:text-stone-400 italic whitespace-pre-wrap"
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}

            {prayer.closing && (
              <div className="space-y-2 pt-2 border-t border-stone-100 dark:border-stone-800">
                {prayer.closing.map((line, i) => (
                  <p
                    key={`c-${i}`}
                    className="text-[14px] leading-[1.8] text-stone-600 dark:text-stone-400 whitespace-pre-wrap"
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
